import { Bell, Settings } from 'lucide-react'
import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'

import { surfaceClass, useDismissable } from '@/components/layout/ui'
import { ErrorBanner, Spinner, cn } from '@/features/issue'
import { useWorkspace } from '@/lib/workspace'

import { NotificationRow } from './NotificationRow'
import { useNotificationFeed, useNotificationMutations, useUnreadNotificationCount } from './hooks'

/**
 * The bell in the top bar: an unread count and a panel of the latest
 * notifications, with a link to the full page and to the preferences.
 */
export function NotificationBell() {
  const workspace = useWorkspace()
  const slug = workspace.slug
  const [open, setOpen] = useState(false)
  const [unreadOnly, setUnreadOnly] = useState(false)
  const close = useCallback(() => setOpen(false), [])
  const ref = useDismissable(open, close)

  const unread = useUnreadNotificationCount(slug)
  const feed = useNotificationFeed(slug, unreadOnly, open)
  const { markRead, markUnread, markAllRead } = useNotificationMutations(slug)

  const count = unread.data ?? 0
  const items = feed.data?.pages.flatMap((page) => page.data) ?? []

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={count > 0 ? `Notifications, ${count} unread` : 'Notifications'}
        className="relative rounded-md p-2 text-ink-600 transition hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-ink-800"
      >
        <Bell className="size-5" aria-hidden="true" />
        {count > 0 ? (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold text-white">
            {count > 99 ? '99+' : count}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Notifications"
          className={cn(surfaceClass, 'absolute top-full right-0 z-30 mt-1.5 w-[22rem] max-w-[calc(100vw-1.5rem)] shadow-lg shadow-ink-950/10')}
        >
          <div className="flex items-center gap-2 border-b border-ink-200 px-3 py-2 dark:border-ink-800">
            <p className="text-sm font-semibold">Notifications</p>
            <label className="ml-auto flex items-center gap-1.5 text-xs text-ink-500">
              <input
                type="checkbox"
                checked={unreadOnly}
                onChange={(event) => setUnreadOnly(event.target.checked)}
                className="size-3.5 accent-brand-600"
              />
              Unread only
            </label>
            <button
              type="button"
              onClick={() => markAllRead.mutate()}
              disabled={count === 0 || markAllRead.isPending}
              className="text-xs font-medium text-brand-700 hover:underline disabled:opacity-40 dark:text-brand-300"
            >
              Mark all read
            </button>
          </div>

          <div className="max-h-[60vh] overflow-y-auto p-1.5">
            {feed.isPending ? (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            ) : feed.isError ? (
              <ErrorBanner error={feed.error} onRetry={() => void feed.refetch()} className="m-1.5" />
            ) : items.length === 0 ? (
              <p className="px-3 py-8 text-center text-sm text-ink-500">
                {unreadOnly ? 'Nothing unread.' : 'No notifications yet.'}
              </p>
            ) : (
              <>
                {items.map((notification) => (
                  <NotificationRow
                    key={notification.id}
                    notification={notification}
                    compact
                    onOpen={() => {
                      if (!notification.read_at) markRead.mutate(notification.id)
                      close()
                    }}
                    onToggleRead={() =>
                      notification.read_at ? markUnread.mutate(notification.id) : markRead.mutate(notification.id)
                    }
                  />
                ))}
                {feed.hasNextPage ? (
                  <button
                    type="button"
                    onClick={() => void feed.fetchNextPage()}
                    disabled={feed.isFetchingNextPage}
                    className="mt-1 w-full rounded-md py-1.5 text-xs text-ink-500 hover:bg-ink-100 dark:hover:bg-ink-800"
                  >
                    {feed.isFetchingNextPage ? 'Loading…' : 'Show older'}
                  </button>
                ) : null}
              </>
            )}
          </div>

          <div className="flex items-center justify-between border-t border-ink-200 px-3 py-2 text-xs dark:border-ink-800">
            <Link to={workspace.path('/notifications')} onClick={close} className="font-medium text-brand-700 hover:underline dark:text-brand-300">
              See all
            </Link>
            <Link
              to={workspace.path('/settings/notifications')}
              onClick={close}
              className="flex items-center gap-1 text-ink-500 hover:text-ink-800 dark:hover:text-ink-200"
            >
              <Settings className="size-3.5" aria-hidden="true" />
              Preferences
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  )
}
